/**
 * Custom hook for storage quota management
 *
 * Supports both V2 (fixed per-plan) and V3.1 (isolated per-view) models.
 * V3.1: each view has its own quota from tier + addOnStorageBytes.
 * Aggregate shown in sidebar: sum of all views' used / sum of all quotas.
 */

import { useState, useEffect } from "react";
import { collection, getDocs, doc, getDoc } from "firebase/firestore";
import { db } from "@/config/firebase";
import { useEffectiveUserId } from "./useEffectiveUserId";
import { useUserData } from "./useUserData";
import { usePlansConfig } from "./usePlansConfig";
import { PlansConfigV3, UserPlanV3, ViewLicense } from "@/types";

export interface StorageQuota {
  usedBytes: number;
  totalBytes: number;
  usedGB: number;
  totalGB: number;
  percentage: number;
  planName: string;
  loading: boolean;
  isV3: boolean;
  v3Breakdown?: {
    activeViews: number;
  };
}

const FALLBACK_TOTAL_BYTES = 5 * 1024 * 1024 * 1024;
const FALLBACK_PLAN_NAME = "Free Beta";

async function countActualStorageBytes(userId: string): Promise<number> {
  const projectsRef = collection(db, `users/${userId}/projects`);
  const projectsSnap = await getDocs(projectsRef);

  let totalBytes = 0;
  for (const projectDoc of projectsSnap.docs) {
    const viewsRef = collection(db, `users/${userId}/projects/${projectDoc.id}/views`);
    const viewsSnap = await getDocs(viewsRef);
    for (const viewDoc of viewsSnap.docs) {
      const stats = viewDoc.data().stats;
      totalBytes += stats?.storageBytes ?? (stats?.storageMB ?? 0) * 1024 * 1024;
    }
  }
  return totalBytes;
}

async function getPlansConfigV3(): Promise<PlansConfigV3 | null> {
  try {
    const configDoc = await getDoc(doc(db, "config", "plans_v3"));
    if (!configDoc.exists()) return null;
    return configDoc.data() as PlansConfigV3;
  } catch {
    return null;
  }
}

async function getViewLicensesQuota(userId: string, config: PlansConfigV3): Promise<{ totalQuota: number; activeCount: number }> {
  const licensesRef = collection(db, `users/${userId}/viewLicenses`);
  const licensesSnap = await getDocs(licensesRef);

  let totalQuota = 0;
  let activeCount = 0;

  for (const licDoc of licensesSnap.docs) {
    const license = licDoc.data() as ViewLicense;
    if (license.status === "active" || license.status === "cancelling") {
      const tierConfig = config.plans[license.tier];
      const tierStorage = tierConfig?.perView?.storageBytes || 0;
      totalQuota += tierStorage + (license.addOnStorageBytes || 0);
      activeCount++;
    }
  }

  return { totalQuota, activeCount };
}

export function useStorageQuota(): StorageQuota {
  const { effectiveUid } = useEffectiveUserId();
  const { userData, loading: userLoading } = useUserData();
  const { plansConfig, loading: plansLoading } = usePlansConfig();
  const [actualBytes, setActualBytes] = useState<number | null>(null);
  const [countingDone, setCountingDone] = useState(false);
  const [v3Config, setV3Config] = useState<PlansConfigV3 | null>(null);
  const [v3Quota, setV3Quota] = useState<{ totalQuota: number; activeCount: number } | null>(null);

  const planV3 = (userData as any)?.planV3 as UserPlanV3 | undefined;
  const isV3 = !!planV3;
  const isBeta = planV3?.tier === "beta";
  const BETA_STORAGE_CAP_DEFAULT = 500 * 1024 * 1024 * 1024;

  useEffect(() => {
    if (isV3 && !isBeta) {
      getPlansConfigV3().then(setV3Config);
    }
  }, [isV3, isBeta]);

  useEffect(() => {
    if (!effectiveUid || !isV3 || !v3Config) return;

    let cancelled = false;
    getViewLicensesQuota(effectiveUid, v3Config).then((q) => {
      if (!cancelled) setV3Quota(q);
    });
    return () => { cancelled = true; };
  }, [effectiveUid, isV3, v3Config]);

  useEffect(() => {
    if (!effectiveUid) {
      setActualBytes(null);
      setCountingDone(true);
      return;
    }

    let cancelled = false;
    countActualStorageBytes(effectiveUid).then((bytes) => {
      if (!cancelled) {
        setActualBytes(bytes);
        setCountingDone(true);
      }
    }).catch(() => {
      if (!cancelled) setCountingDone(true);
    });

    return () => { cancelled = true; };
  }, [effectiveUid]);

  useEffect(() => {
    if (!effectiveUid || !userData) return;

    let cancelled = false;
    countActualStorageBytes(effectiveUid).then((bytes) => {
      if (!cancelled) setActualBytes(bytes);
    }).catch(() => {});

    return () => { cancelled = true; };
  }, [effectiveUid, userData?.usage?.totalStorageBytes]);

  const loading = userLoading || plansLoading || !countingDone;
  const usedBytes = actualBytes ?? userData?.usage?.totalStorageBytes ?? 0;

  let totalBytes: number;
  let planName: string;
  let v3Breakdown: StorageQuota["v3Breakdown"];

  if (isBeta) {
    totalBytes = planV3?.betaStorageCapBytes || BETA_STORAGE_CAP_DEFAULT;
    planName = "Beta User";
  } else if (isV3 && v3Config && v3Quota) {
    totalBytes = v3Quota.totalQuota || FALLBACK_TOTAL_BYTES;
    // The per-view model has no account-level plan name: the account is the sum
    // of its view licenses, so name it by how many views it covers.
    planName = `${v3Quota.activeCount} view${v3Quota.activeCount !== 1 ? "s" : ""}`;
    v3Breakdown = { activeViews: v3Quota.activeCount };
  } else if (isV3) {
    // No license on any view. Every view is born with a free license (created by
    // the onViewCreate trigger), so this is either an account with no views yet
    // or a license still in flight — not a plan-less state.
    totalBytes = 0;
    planName = "no views";
    v3Breakdown = { activeViews: 0 };
  } else {
    // Legacy V2 accounts. `plan` was sometimes written as a bare string ("free")
    // rather than an object, in which case `plan.tier` is undefined — so read
    // both shapes before falling back.
    const rawTier =
      typeof userData?.plan === "string" ? userData.plan : userData?.plan?.tier;
    const tier = (rawTier ?? "free") as keyof NonNullable<
      typeof plansConfig
    >["plans"];
    totalBytes = plansConfig?.plans?.[tier]?.limits?.storageBytes ?? FALLBACK_TOTAL_BYTES;
    planName = plansConfig?.plans?.[tier]?.name ?? FALLBACK_PLAN_NAME;
  }

  const usedGB = usedBytes / (1024 * 1024 * 1024);
  const totalGB = totalBytes / (1024 * 1024 * 1024);
  const percentage =
    totalBytes > 0 ? Math.min(Math.round((usedBytes / totalBytes) * 100), 100) : 0;

  return {
    usedBytes,
    totalBytes,
    usedGB,
    totalGB,
    percentage,
    planName,
    loading,
    isV3,
    v3Breakdown,
  };
}
